interface GaugeComponentProps {
  data: any
  valueKey: string
  title?: string
  min?: number
  max?: number
  unit?: string
  thresholds?: Array<{
    value: number
    color: string
    label: string
  }>
  loading?: boolean
}

// Thresholds por defecto (0-100)
const DEFAULT_THRESHOLDS = [
  { value: 0, color: '#10b981', label: 'Bajo' },
  { value: 33, color: '#f59e0b', label: 'Medio' },
  { value: 66, color: '#ef4444', label: 'Alto' },
]

export function GaugeComponent({
  data,
  valueKey,
  title,
  min = 0,
  max = 100,
  unit = '',
  thresholds = DEFAULT_THRESHOLDS,
  loading = false,
}: GaugeComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[250px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  // Extraer valor
  let value: number
  if (typeof data === 'number') {
    value = data
  } else if (data && typeof data === 'object' && valueKey in data) {
    value = Number(data[valueKey])
  } else if (Array.isArray(data) && data.length > 0 && valueKey in data[0]) {
    value = Number(data[0][valueKey])
  } else {
    value = 0
  }

  // Validar valor
  if (isNaN(value)) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[250px] flex items-center justify-center text-gray-500">
          Valor inválido
        </div>
      </div>
    )
  }

  // Clamp value entre min y max
  const clampedValue = Math.max(min, Math.min(max, value))
  const percentage = ((clampedValue - min) / (max - min)) * 100

  // Determinar color y label según thresholds
  const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value)
  let currentColor = sortedThresholds[0].color
  let currentLabel = sortedThresholds[0].label

  for (const threshold of sortedThresholds) {
    if (clampedValue >= threshold.value) {
      currentColor = threshold.color
      currentLabel = threshold.label
    }
  }

  // SVG Gauge (semicírculo)
  const radius = 80
  const strokeWidth = 20
  const circumference = Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <div className="flex flex-col items-center justify-center">
        {/* SVG Gauge */}
        <div className="relative w-[220px] h-[140px]">
          <svg
            width="220"
            height="140"
            viewBox="0 0 220 140"
            className="overflow-visible"
          >
            {/* Background arc */}
            <path
              d={`M 20 120 A ${radius} ${radius} 0 0 1 200 120`}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />

            {/* Progress arc */}
            <path
              d={`M 20 120 A ${radius} ${radius} 0 0 1 200 120`}
              fill="none"
              stroke={currentColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: 'stroke-dashoffset 1s ease-in-out',
              }}
            />
          </svg>

          {/* Valor central */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
            <div className="text-4xl font-bold" style={{ color: currentColor }}>
              {clampedValue.toLocaleString('es-ES', {
                maximumFractionDigits: 1,
              })}
            </div>
            {unit && <div className="text-lg text-gray-500 mt-1">{unit}</div>}
          </div>
        </div>

        {/* Label de estado */}
        <div className="mt-4 text-center">
          <span
            className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white"
            style={{ backgroundColor: currentColor }}
          >
            {currentLabel}
          </span>
        </div>

        {/* Rango min-max */}
        <div className="mt-4 w-full max-w-[200px] flex justify-between text-xs text-gray-500">
          <span>{min}</span>
          <span>{max}</span>
        </div>

        {/* Leyenda de thresholds */}
        <div className="mt-4 grid grid-cols-3 gap-2 w-full max-w-[250px]">
          {sortedThresholds.map((threshold, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: threshold.color }}
              ></div>
              <span className="text-gray-600">{threshold.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
